import { useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { useMasteryCutscene } from "@/state/masteryCutscene";
import { NarrativeBeatPlayer } from "@/components/NarrativeBeatPlayer";
import type { NarrativeBeat } from "@/api/types";
import { AudioManager } from "@/engine/AudioManager";

/**
 * 마스터 졸업 컷신 오버레이. App 루트에 한 번만 마운트.
 *
 * <p>2026-04-23 v2: 서/혼 Lv 5 달성 시 큐에 들어온 MasteryEvent 를 NarrativeBeatPlayer
 * 로 재생. 모든 비트 소진 시 다음 항목 승급.
 *
 * <p>기존 StoryFlashOverlay (단순 토스트) 대비 서사 비중 높은 순간 전용. 풀스크린 dim
 * + 제목 배너 + 비트 순차 재생.
 */
export function MasteryCutsceneOverlay() {
    const head = useMasteryCutscene((s) => s.queue[0]);
    const shift = useMasteryCutscene((s) => s.shift);

    useEffect(() => {
        if (!head) return;
        AudioManager.sfx(tokens.audio.sfx.sigilPulse, 0.8);
    }, [head]);

    const beats: NarrativeBeat[] | null = useMemo(() => {
        if (!head) return null;
        return head.event.beats.map((b, idx) => ({
            type: b.type,
            speaker: b.speaker,
            text: b.text,
            variant: "memorial",
            mood: head.event.tone ?? "hushed",
            order: idx,
        } as NarrativeBeat));
    }, [head]);

    if (!head || !beats) return null;

    const title = head.event.title || `${head.pathLabel}의 ${head.groupLabel} · 마스터`;
    const subtitle = head.event.subtitle || "";

    return (
        <AnimatePresence>
            <motion.div
                key={head.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="fixed inset-0 flex items-center justify-center"
                style={{
                    zIndex: tokens.elevation.modal + 20,
                    background: `radial-gradient(circle at 50% 42%, ${tokens.color.burgundy}22 0%, ${tokens.color.ink}EE 38%, ${tokens.color.ink}F8 100%)`,
                    backdropFilter: "blur(12px)",
                }}
            >
                <div className="w-full max-w-[820px] px-xl">
                    {/* 헤더 */}
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: tokens.easing.decelerate }}
                        className="text-center mb-l"
                    >
                        <div
                            className="font-body flex items-center justify-center gap-s"
                            style={{
                                color: tokens.color.burgundy,
                                fontSize: 11,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                opacity: 0.92,
                            }}
                        >
                            <motion.span
                                aria-hidden
                                animate={{ opacity: [0.5, 1, 0.5] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                                style={{
                                    width: 16, height: 1,
                                    background: tokens.color.burgundy,
                                    boxShadow: `0 0 8px ${tokens.color.burgundy}`,
                                }}
                            />
                            Mastery · {head.pathLabel}의 {head.groupLabel}
                            <motion.span
                                aria-hidden
                                animate={{ opacity: [0.5, 1, 0.5] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                                style={{
                                    width: 16, height: 1,
                                    background: tokens.color.burgundy,
                                    boxShadow: `0 0 8px ${tokens.color.burgundy}`,
                                }}
                            />
                        </div>
                        <motion.div
                            className="font-display mt-s"
                            initial={{ opacity: 0, letterSpacing: "0.5em" }}
                            animate={{ opacity: 1, letterSpacing: "0.04em" }}
                            transition={{ delay: 0.2, duration: 0.9, ease: tokens.easing.decelerate }}
                            style={{
                                color: tokens.color.parchment,
                                fontSize: "clamp(28px, 3.2vw, 44px)",
                                textShadow: `0 0 28px ${tokens.color.burgundy}99, 0 2px 10px ${tokens.color.ink}`,
                            }}
                        >
                            {title}
                        </motion.div>
                        {subtitle && (
                            <motion.div
                                className="mt-s font-body italic"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 0.88 }}
                                transition={{ delay: 0.5, duration: 0.6 }}
                                style={{
                                    color: tokens.color.parchmentDim,
                                    fontSize: tokens.type.size.small,
                                    letterSpacing: "0.04em",
                                }}
                            >
                                {subtitle}
                            </motion.div>
                        )}
                        <motion.div
                            aria-hidden
                            className="mx-auto mt-m"
                            initial={{ width: "10%", opacity: 0 }}
                            animate={{ width: "50%", opacity: 0.75 }}
                            transition={{ delay: 0.3, duration: 0.8 }}
                            style={{
                                height: 1,
                                background: `linear-gradient(90deg, transparent, ${tokens.color.burgundy}FF, transparent)`,
                                boxShadow: `0 0 16px ${tokens.color.burgundy}AA`,
                            }}
                        />
                    </motion.div>

                    {/* NarrativeBeatPlayer */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6, ease: tokens.easing.decelerate }}
                    >
                        <NarrativeBeatPlayer
                            beats={beats}
                            theme="soul"
                            onComplete={() => {
                                AudioManager.sfx(tokens.audio.sfx.sigilPulse, 0.6);
                                shift();
                            }}
                        />
                    </motion.div>

                    {/* 보상 힌트 */}
                    {head.event.reward?.title && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.86 }}
                            transition={{ delay: 1.2, duration: 0.5 }}
                            className="mt-m text-center font-body"
                            style={{
                                color: tokens.color.brass,
                                fontSize: tokens.type.size.small,
                                letterSpacing: tokens.type.letterSpacing.wide,
                            }}
                        >
                            ✦ 보상 · <span style={{ color: tokens.color.parchment }}>{head.event.reward.title}</span>
                        </motion.div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
